import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';


import { PublicRegistrationService } from '../../../service/public/public-registration.service';
import { ToasterService } from '../../../service/common/toaster.service';
import { PatientAgeFieldsComponent } from '../../shared/patient-age-fields/patient-age-fields.component';
import {
  formatPatientAge,
  hasValidPatientAge,
  patientAgeToPayload,
} from '../../../utils/patient-age.util';

type Step = 'identify' | 'register' | 'found' | 'submitted' | 'alreadyPending';

@Component({
  selector: 'app-patient-self-registration',
  standalone: true,
  imports: [CommonModule, FormsModule, ToastModule, PatientAgeFieldsComponent],
  providers: [PublicRegistrationService],
  templateUrl: './patient-self-registration.component.html',
  styleUrl: './patient-self-registration.component.scss',
})
export class PatientSelfRegistrationComponent {
  step: Step = 'identify';

  checking = false;
  submitting = false;

  readonly salutations = ['Mr.', 'Mrs.', 'Ms.', 'Shri.', 'Smt.', 'Ku.', 'Dr.'];

  identify = {
    full_name: '',
    contact_number: '',
  };

  form = {
    salutation: '' as string,
    full_name: '',
    age: '' as number | string,
    age_months: '' as number | string,
    age_days: '' as number | string,
    contact_number: '',
    email: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    occupation: '',
  };

  // Honeypot: must stay empty. Bots that auto-fill every field will trip this.
  honeypot = '';

  result: {
    full_name: string;
    patient_number: string;
  } | null = null;

  constructor(
    private registrationService: PublicRegistrationService,
    private toaster: ToasterService) {}

  get sanitizedPhone(): string {
    return (this.identify.contact_number || '').replace(/\D/g, '');
  }

  /**
   * A valid full name needs at least two words (first + last), each containing
   * letters. Allows letters, spaces, dots, hyphens and apostrophes.
   */
  isFullName(value: string): boolean {
    const name = (value || '').trim().replace(/\s+/g, ' ');
    return /^\p{L}[\p{L}.'-]*(?:\s+\p{L}[\p{L}.'-]*)+$/u.test(name);
  }

  get identifyNameError(): boolean {
    const name = this.identify.full_name.trim();
    return name.length > 0 && !this.isFullName(name);
  }

  get registerNameError(): boolean {
    const name = this.form.full_name.trim();
    return name.length > 0 && !this.isFullName(name);
  }

  get isIdentifyValid(): boolean {
    return this.isFullName(this.identify.full_name) && this.sanitizedPhone.length >= 10;
  }

  get isRegisterValid(): boolean {
    return (
      this.isFullName(this.form.full_name) &&
      hasValidPatientAge({
        years: this.form.age,
        months: this.form.age_months,
        days: this.form.age_days,
      }) &&
      (this.form.contact_number || '').replace(/\D/g, '').length >= 10
    );
  }

  displayPatientAge(
    years?: number | string | null,
    months?: number | string | null,
    days?: number | string | null,
  ): string {
    return formatPatientAge(years, months, days);
  }

  onPhoneInput(field: 'identify' | 'form'): void {
    if (field === 'identify') {
      this.identify.contact_number = this.identify.contact_number
        .replace(/\D/g, '')
        .slice(0, 15);
    } else {
      this.form.contact_number = this.form.contact_number
        .replace(/\D/g, '')
        .slice(0, 15);
    }
  }

  selectSalutation(value: string): void {
    this.form.salutation = this.form.salutation === value ? '' : value;
  }

  checkPatient(): void {
    if (!this.isIdentifyValid || this.checking) {
      this.toaster.warn('Enter your full name (first and last) and a valid 10-digit mobile number.', 'Almost there');
      return;
    }

    this.checking = true;
    const payload = {
      full_name: this.identify.full_name.trim(),
      contact_number: this.sanitizedPhone,
    };

    this.registrationService.checkPatient(payload).subscribe({
      next: (res) => {
        this.checking = false;
        if (res.exists && res.status === 'registered' && res.data) {
          this.result = res.data;
          this.step = 'found';
        } else if (res.exists && res.status === 'pending') {
          this.step = 'alreadyPending';
        } else {
          this.form.full_name = payload.full_name;
          this.form.contact_number = payload.contact_number;
          this.step = 'register';
        }
      },
      error: () => {
        this.checking = false;
        this.toaster.error('We could not reach the clinic. Please try again.', 'Connection problem');
      },
    });
  }

  submitRegistration(): void {
    if (!this.isRegisterValid || this.submitting) {
      this.toaster.warn(
        'Please enter your full name (first and last), age and mobile number.',
        'Missing details',
      );
      return;
    }

    this.submitting = true;
    const payload = {
      salutation: this.form.salutation || null,
      full_name: this.form.full_name.trim(),
      ...patientAgeToPayload({
        years: this.form.age,
        months: this.form.age_months,
        days: this.form.age_days,
      }),
      contact_number: (this.form.contact_number || '').replace(/\D/g, ''),
      email: this.form.email?.trim() || null,
      address: this.form.address?.trim() || null,
      city: this.form.city?.trim() || null,
      state: this.form.state?.trim() || null,
      pincode: this.form.pincode?.trim() || null,
      occupation: this.form.occupation?.trim() || null,
      company: this.honeypot,
    };

    this.registrationService.registerPatient(payload).subscribe({
      next: (res) => {
        this.submitting = false;
        // Existing live patient -> show their number.
        if (res.status === 'registered' && res.data) {
          this.result = res.data;
          this.step = 'found';
          return;
        }
        // Submitted (new or already pending) -> confirmation screen.
        if (res.success) {
          this.step = 'submitted';
          return;
        }
        this.toaster.error(res.message || 'Please try again.', 'Could not register');
      },
      error: (err) => {
        this.submitting = false;
        if (err?.status === 409 && err?.error?.data) {
          this.result = err.error.data;
          this.step = 'found';
          return;
        }
        const detail =
          err?.error?.message ||
          'Something went wrong. Please ask at the reception desk.';
        this.toaster.add({
          severity: 'error',
          summary: 'Could not register',
          detail,
        });
      },
    });
  }

  backToStart(): void {
    this.step = 'identify';
    this.result = null;
    this.honeypot = '';
    this.identify = { full_name: '', contact_number: '' };
    this.form = {
      salutation: '',
      full_name: '',
      age: '',
      age_months: '',
      age_days: '',
      contact_number: '',
      email: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      occupation: '',
    };
  }

  editDetails(): void {
    this.step = 'register';
  }
}
